import { Paper, UnstyledButton } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { CreatorAgentSuccessFlowStepKey as AgentSuccessStep } from '@newo/tenant-metadata';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import { Play } from 'lucide-react';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import NavigateToPartnerCta from '@/components/NavigateToPartnerCta';
import VideoPlayer from '@/components/VideoPlayer';
import { useHostContext } from '@/context/HostContext.tsx';
import AgentSuccessHeader from '@/pages/AgentSuccess/components/AgentSuccessHeader';
import StepsBlock from '@/pages/AgentSuccess/components/StepsBlock/StepsBlock';
import WaitingVideoModal from '@/pages/AgentSuccess/components/WaitingVideoModal';
import { partnerMetadata } from '@/services/PartnerMetadata';
import { useAgentTraining } from '@/store/agent-training';
import useAppStore from '@/store/app';
import { cn } from '@/utils';
import { CREATOR_VIDEO_MAP_KEYS } from '@/utils/creatorVideoKeys';

const COLLAPSE_DISTANCE_PX = 220;

const AgentSuccess = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { isMiniCreatorMode, localization } = useHostContext();
	const nextStepsVideo = partnerMetadata.getCreatorVideoSource(
		CREATOR_VIDEO_MAP_KEYS.NEXT_STEPS
	);
	const modalVideo = partnerMetadata.getCreatorVideoSource(
		CREATOR_VIDEO_MAP_KEYS.SUCCESS_MODAL
	);
	const agentSuccessConfig = useMemo(
		() => partnerMetadata.getAgentSuccessConfig(),
		[]
	);

	const isMobile = useMediaQuery('(max-width: 640px)');
	const { agentSuccessActiveStep, setAgentSuccessStep } = useAppStore(
		useShallow((state) => ({
			agentSuccessActiveStep: state.agentSuccessActiveStep,
			setAgentSuccessStep: state.setAgentSuccessStep
		}))
	);

	const { setModalOpenedFlag, isModalOpenedFlag } = useAgentTraining();
	const [isOpenedModal, { open, close }] = useDisclosure(
		!isModalOpenedFlag &&
			agentSuccessActiveStep === AgentSuccessStep.ActivateAccount &&
			searchParams.get('checkout') !== 'session_completed'
	);

	const stepsRefContainer = useRef<
		Partial<Record<AgentSuccessStep, HTMLDivElement>>
	>({});

	const scrollContainerRef = useRef<HTMLDivElement | null>(null);

	const { scrollY } = useScroll(
		isMiniCreatorMode ? { container: scrollContainerRef } : undefined
	);

	const rawProgress = useTransform(scrollY, (y) => {
		const p = y / COLLAPSE_DISTANCE_PX;
		if (p <= 0) {
			return 0;
		}
		if (p >= 1) {
			return 1;
		}
		return p;
	});

	const progress = useSpring(rawProgress, {
		stiffness: 220,
		damping: 24,
		mass: 0.8
	});

	const stepsTranslateY = useTransform(progress, [0, 1], [180, 0]);

	const handleClosePopup = () => {
		close();
		setModalOpenedFlag(true);
	};

	const handleOpenPopup = () => {
		open();
		setModalOpenedFlag(true);
	};

	const handleSetCardRef = (
		step: AgentSuccessStep,
		ref: HTMLDivElement | null
	) => {
		if (!ref) {
			return;
		}

		stepsRefContainer.current = {
			...stepsRefContainer.current,
			[step]: ref
		};
	};

	const scrollToStep = (step: AgentSuccessStep) => {
		const el = stepsRefContainer.current[step];
		if (!el) {
			return;
		}

		requestAnimationFrame(() => {
			setTimeout(() => {
				el.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}, 100);
		});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: should run only on first render
	useLayoutEffect(() => {
		if (searchParams.get('checkout') === 'session_completed') {
			const next = agentSuccessConfig?.steps[agentSuccessActiveStep]?.nextStep;
			if (next) {
				setAgentSuccessStep(next);
				scrollToStep(next);
			}
			setSearchParams({}, { replace: true });
		}
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: should run only on first render
	useLayoutEffect(() => {
		const firstKey =
			agentSuccessConfig.stepOrder?.[0] ?? AgentSuccessStep.ActivateAccount;

		if (
			agentSuccessActiveStep !== firstKey &&
			searchParams.get('checkout') !== 'session_completed'
		) {
			scrollToStep(agentSuccessActiveStep);
		}
	}, []);

	return (
		<div className="w-full flex justify-center h-full">
			<Paper
				className={cn(
					'flex flex-col gap-20 sm:gap-[32px] w-full max-w-[1015px] p-5 items-center shadow-xl',
					{
						'md:p-[60px] rounded-[20px] sm:rounded-[40px]': !isMiniCreatorMode,
						'gap-2 px-0 pb-2 pt-0 h-full overflow-y-auto rounded-none':
							isMiniCreatorMode,
						'!gap-0': partnerMetadata.isSeparateActivateButtonEnabled()
					}
				)}
			>
				{isMiniCreatorMode && (
					<NavigateToPartnerCta className="mt-2 w-11/12 rounded-md text-xs border-primary-900 bg-white text-primary-900" />
				)}
				<AgentSuccessHeader progress={progress} />

				{!partnerMetadata.isVideosHidden() && modalVideo && (
					<div
						className={cn('w-full flex justify-center mb-0 sm:mb-[48px]', {
							'm-0': isMiniCreatorMode
						})}
					>
						<UnstyledButton
							className="text-[#495057] text-[14px]"
							onClick={handleOpenPopup}
						>
							<div className="flex items-center">
								<Play className="pr-[8px]" />
								<span className="underline">
									{localization.translate(
										'creator',
										'agent_success.watch_video_cta'
									)}
								</span>
							</div>
						</UnstyledButton>
					</div>
				)}

				<div
					className={cn({
						'flex-1 w-full min-h-0 px-2': isMiniCreatorMode
					})}
					ref={scrollContainerRef}
				>
					{!!nextStepsVideo?.trim() && (
						<VideoPlayer
							className="border !border-primary-900 !rounded-2xl"
							source={nextStepsVideo}
						/>
					)}

					{!partnerMetadata.isStepsBlockHidden() && (
						<motion.div
							style={
								isMobile || isMiniCreatorMode ? {} : { y: stepsTranslateY }
							}
						>
							<StepsBlock handleSetCardRef={handleSetCardRef} />
						</motion.div>
					)}
				</div>
			</Paper>
			<WaitingVideoModal
				closeModal={handleClosePopup}
				isOpenedModal={isOpenedModal}
			/>
		</div>
	);
};

export default AgentSuccess;
